// The dashboard's only way to talk to ICE: /dashboard/api, same origin, with ICE's cookies.

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  constructor(status: number, code: string, message: string) {
    super(message)
    this.status = status
    this.code = code
  }
}

/** Where to go when ICE says the session has ended. */
export const SIGN_IN = '/dashboard/signin'

async function request<T>(method: string, path: string, body?: unknown, contentType?: string): Promise<T> {
  const headers: Record<string, string> = {}
  // ICE refuses changes without this header (server/src/dashboard/api.ts): a page on another
  // site can't send it without asking first, and ICE never says yes.
  if (method !== 'GET') headers['X-ICE-Dashboard'] = '1'
  let payload: BodyInit | undefined
  if (body instanceof Blob) {
    payload = body
    headers['Content-Type'] = contentType ?? body.type
  } else if (body !== undefined) {
    payload = JSON.stringify(body)
    headers['Content-Type'] = 'application/json'
  }
  const res = await fetch(`/dashboard/api${path}`, { method, headers, body: payload, credentials: 'same-origin' })
  if (res.status === 401) {
    window.location.assign(SIGN_IN)
    throw new ApiError(401, 'UNAUTHENTICATED', 'Signing you in…')
  }
  const data = res.headers.get('Content-Type')?.includes('application/json') ? await res.json() : null
  if (!res.ok) {
    const error = (data as { error?: { code?: string; message?: string } } | null)?.error
    throw new ApiError(res.status, error?.code ?? 'ERROR', error?.message ?? `Something went wrong (${res.status})`)
  }
  return data as T
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body: unknown, contentType?: string) => request<T>('PUT', path, body, contentType),
  delete: <T>(path: string) => request<T>('DELETE', path),
}

export function errorText(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong'
}
